/**
 * @fileoverview Fixed-width column layout for monospace receipts.
 *
 * Extracted verbatim from `backend/src/utils/receiptFormatter.js`. The till
 * prints these strings to a 48-column thermal printer; the cloud regenerates the
 * same documents from persisted rows. Two copies of this arithmetic would drift,
 * and the drift would only ever show up as a customer holding two receipts for
 * the same sale that do not line up.
 *
 * The behaviours below look like bugs and are not — they are what the till has
 * printed for its whole life, and a regenerated receipt has to match. They are
 * documented rather than fixed.
 *
 * Only the pure layout functions live here. `buildStoreHeading` reads
 * `process.env` and `toDateTimeParts` reads the system locale, so both stay in
 * the till where that context exists; the cloud supplies its own, because a
 * server in one timezone must not stamp a receipt for a store in another.
 */

/**
 * Pads to `width`, truncating with an ellipsis when the text does not fit.
 *
 * The truncation keeps `width - 3` characters and appends three dots, so the
 * result fills the column exactly — except when `width < 3`, where `substring`
 * clamps a negative end to 0 and the result is a bare `'...'` that overflows.
 * No caller uses a width that small.
 *
 * @param {string} str
 * @param {number} width
 * @returns {string}
 */
function formatLeft(str, width) {
  if (str.length > width) {
    return str.substring(0, width - 3) + '...';
  }
  return str.padEnd(width);
}

/**
 * Pads to `width` on the left.
 *
 * Overflow keeps the **tail**, not the head — for a currency figure that means
 * losing the most significant digits. It is deliberate: these columns hold
 * money, and a truncated total is more obviously wrong to a reader than a
 * plausible-looking smaller one. Widths are sized so it does not happen.
 *
 * @param {string} str
 * @param {number} width
 * @returns {string}
 */
function formatRight(str, width) {
  if (str.length > width) {
    return str.substring(str.length - width);
  }
  return str.padStart(width);
}

/**
 * Centres within `width`.
 *
 * Does not truncate: text at or over the width is returned unchanged and the
 * line runs long. Store headings go through here, and a wrapped shop name reads
 * better than a clipped one.
 *
 * @param {string} str
 * @param {number} width
 * @returns {string}
 */
function formatCenter(str, width) {
  if (str.length >= width) return str;
  const leftPadding = Math.floor((width - str.length) / 2);
  const rightPadding = width - str.length - leftPadding;
  return ' '.repeat(leftPadding) + str + ' '.repeat(rightPadding);
}

/**
 * Total line width for a column map.
 *
 * @param {Record<string, number>} columnWidths
 * @returns {number}
 */
function calculateLineLength(columnWidths) {
  return Object.values(columnWidths).reduce((acc, width) => acc + width, 0);
}

/**
 * The two horizontal rules a receipt is built from.
 *
 * @param {number} lineLength
 * @param {{ headerChar?: string, dividerChar?: string }} [options]
 * @returns {{ headerFooter: string, divider: string }}
 */
function getLineSeparators(lineLength, options = {}) {
  const { headerChar = '=', dividerChar = '-' } = options;
  return {
    headerFooter: headerChar.repeat(lineLength),
    divider: dividerChar.repeat(lineLength),
  };
}

/**
 * Strips characters a thermal printer cannot render.
 *
 * The till applies this to product and customer names on the way out, so the
 * cloud has to apply the same rule or a regenerated receipt would show a
 * character the original could not print. Keeps printable ASCII plus the pound
 * sign, and collapses the surrounding whitespace that removal leaves behind.
 *
 * @param {string} value
 * @returns {string}
 */
function sanitisePrintable(value) {
  // eslint-disable-next-line no-control-regex -- the point is to name the range
  return String(value).replace(/[^\x20-\x7E£]/g, '').trim();
}

module.exports = {
  formatLeft,
  formatRight,
  formatCenter,
  calculateLineLength,
  getLineSeparators,
  sanitisePrintable,
};
